import type { Entity } from '@backstage/catalog-model';
import type { ComponentProps } from 'react';

import { Card } from '@ipen-portal/plugin-common-react';
import { useState } from 'react';

import { HomeCardEntityList } from './HomeCardEntityList';
import { HomeCardPagination } from './HomeCardPagination';

type Props<T extends Entity> = Pick<ComponentProps<typeof Card>, 'className' | 'headerActions' | 'title'> & {
  'aria-label': string;
  'entities'?: T[];
  'footerAction'?: ComponentProps<typeof Card>['footer'];
  'loading': boolean;
  'message'?: string;
  'showImages'?: boolean;
};

export const HomeEntityCard = <T extends Entity>({
  'aria-label': ariaLabel,
  className,
  entities,
  footerAction,
  headerActions,
  loading,
  message,
  showImages = false,
  title,
}: Props<T>) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(0);
  const totalItems = entities?.length ?? 0;
  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;
  const currentPage = Math.min(page, Math.max(totalPages, 1));
  if (page !== currentPage) setPage(currentPage);

  return (
    <Card
      className={className}
      footer={(
        <>
          {footerAction}
          <HomeCardPagination
            currentPage={currentPage}
            onPageChange={setPage}
            pageSize={pageSize}
            totalItems={totalItems}
          />
        </>
      )}
      headerActions={headerActions}
      title={title}
    >
      <HomeCardEntityList
        aria-label={ariaLabel}
        entities={entities?.slice((currentPage - 1) * pageSize, currentPage * pageSize) ?? []}
        loading={loading}
        message={message}
        onPageSizeChange={setPageSize}
        pageSize={pageSize}
        showImages={showImages}
      />
    </Card>
  );
};
